import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, ArrowRight } from 'lucide-react';
import { useDbNavigate } from '../../context/DatabaseContext';
import { useNavigation } from '../../context/NavigationContext';

// Tile order follows the Utilities navigation rows so the dashboard and the sidebar read the
// same way. A tile shows when its tool's navigation row (found by slug) is in the person's tree.
//
// A tool marked `global: true` has a slug-free route, so opening it skips the database slug
// (openTool below).
const toolGroups = [
  {
    id: 'utilities-tools',
    label: 'Utilities',
    icon: Wrench,
    tools: [
      {
        slug: 'import-template',
        label: 'Bulk Import',
        description: 'Import records in bulk via Excel template. Validates data against existing records, resolves FK display values to IDs, and executes INSERT/UPDATE operations across all configured tables.'
      },
      {
        slug: 'power-query',
        label: 'Power Query',
        description: 'Generates an Excel workbook of project data, one tab per database view. Tabs include Drawings, Drawing Log, Index Sheets, Panels, Tags, IP addressing, networking and power distribution.'
      },
      {
        slug: 'io-count-by-panel',
        label: 'IO Count By Panel',
        description: 'Summarizes IO point counts per panel by card and panel type, with a configurable spare threshold to flag panels running short on spare capacity.'
      },
      {
        slug: 'distance-approximation',
        label: 'Distance Approximation',
        description: 'Approximates distances from a target location using building column grid spacing. Results come back as a filterable, sortable grid with configurable columns.'
      },
      {
        slug: 'AudTrl-CRUD',
        label: 'Audit Trail - CRUD',
        description: 'Browse audit trail records by table to review create, update and delete history, and remove entries that are no longer needed.'
      },
      {
        slug: 'estimate-export',
        label: 'Estimate Export',
        description: 'Builds a Bluebeam takeoff and panel matrix from project data. Handles forced assignments, SCADA and VFD routing, consolidation, and standard unit rates.'
      },
      {
        slug: 'ds-Master-Composer',
        label: 'Master Datasheet Composer',
        global: true,
        description: 'Composes the master catalog of ISA datasheet templates held in the admin database. Works across every project database, so access is granted globally rather than per instance.'
      },
      {
        slug: 'ds-project-composer',
        label: 'Project Datasheet Composer',
        description: "This database's Index Sheet Datasheets: create and revise each Index Sheet's own datasheet from a published ISA Template, and fill in its values."
      },
      {
        slug: 'device-spec-utility',
        label: 'Device Specification Utility',
        description: 'Build Index Sheets and choose device and panel specifications for this database, in lifecycle order: Preferred, Active, then Obsolete.'
      }
    ]
  }
];

const UtilitiesDashboardPage = () => {
  const navigate = useDbNavigate();
  const rawNavigate = useNavigate();
  const { canOpen } = useNavigation();
  const canAccess = (tool) => canOpen(tool.slug);

  // Globally-granted tools live on slug-free routes, so useDbNavigate would send them to
  // /:dbSlug/<slug> and 404.
  const openTool = (tool) => (tool.global ? rawNavigate(`/${tool.slug}`) : navigate(`/${tool.slug}`));

  const visibleGroups = toolGroups
    .map(group => ({ ...group, tools: group.tools.filter(canAccess) }))
    .filter(group => group.tools.length > 0);

  const totalTools = visibleGroups.reduce(
    (sum, g) => sum + g.tools.filter(t => !t.future).length, 0
  );

  return (
    <div className="stui-dashboard-body">
      <div className="stui-dashboard-header">
        <Wrench size={20} className="stui-dashboard-header-icon" />
        <h1 className="stui-dashboard-header-title">Utilities</h1>
        <span className="stui-dashboard-header-subtitle">
          {totalTools} {totalTools === 1 ? 'tool' : 'tools'} available
        </span>
      </div>

      <div className="stui-dashboard-groups">
        {visibleGroups.map(group => {
          const GroupIcon = group.icon;
          const activeTools = group.tools.filter(t => !t.future);
          return (
            <div key={group.id}>
              <div className="stui-dashboard-group-header">
                <GroupIcon size={16} className="stui-dashboard-header-icon" />
                <span className="stui-dashboard-group-title">{group.label}</span>
                <span className="stui-dashboard-group-count">
                  {activeTools.length} {activeTools.length === 1 ? 'tool' : 'tools'} available
                </span>
              </div>
              <div className="stui-dashboard-tile-grid">
                {group.tools.map(tool => (
                  <div
                    key={tool.slug}
                    className={`stui-tile${tool.future ? ' disabled' : ' stui-clickable'}`}
                    onClick={() => !tool.future && openTool(tool)}
                  >
                    <div className="stui-tile-header">
                      <span className="stui-tile-title">{tool.label}</span>
                      {tool.future
                        ? <span className="stui-dashboard-tile-badge">Coming soon</span>
                        : <ArrowRight size={16} className="stui-dashboard-tile-action" />
                      }
                    </div>
                    <div className="stui-dashboard-tile-content">
                      <p className="stui-dashboard-tile-description">{tool.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UtilitiesDashboardPage;
